/** @format */

import { Switch } from "@headlessui/react";

import { useState } from "react";

import { useNotificationPreferences } from "../hooks/useNotificationPreferences";
import {
  useDeviceSubscriptions,
  usePushNotifications,
} from "../hooks/usePushNotifications";
import { useNotificationToastStore } from "../stores/notificationToastStore";
import LoadingCircle from "../utils/LoadingCircle";

// ---------------------------------------------------------------------------
// Reusable SegmentedControl
// ---------------------------------------------------------------------------

function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg bg-slate-800 p-0.5 gap-0.5">
      {options.map(opt => (
        <button
          key={String(opt.value)}
          type="button"
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            opt.value === value
              ? "bg-cPurple-600 text-white"
              : "text-slate-400 hover:text-slate-200"
          }`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle row helper
// ---------------------------------------------------------------------------

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="min-w-0">
        <p className="text-sm text-slate-200">{label}</p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <Switch
        checked={checked}
        onChange={onChange}
        className={`${
          checked ? "bg-green-500" : "bg-slate-600"
        } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cPurple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900`}
      >
        <span
          className={`${
            checked ? "translate-x-5" : "translate-x-0"
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out`}
        />
      </Switch>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category definitions
// ---------------------------------------------------------------------------

const CATEGORY_GROUPS = [
  {
    title: "Session Events",
    categories: [
      {
        id: "race_start",
        label: "Race Start",
        desc: "Get notified when the green flag drops",
      },
      { id: "race_end", label: "Race End", desc: "Results and winner announcement" },
      {
        id: "qualifying_start",
        label: "Qualifying Start",
        desc: "Qualifying session is underway",
      },
      {
        id: "qualifying_end",
        label: "Qualifying End",
        desc: "Final qualifying results",
      },
    ],
  },
  {
    title: "Race Action",
    categories: [
      {
        id: "race_incident",
        label: "Race Incidents",
        desc: "Caution flags, red flags, stage completions, overtime",
      },
      {
        id: "lead_change",
        label: "Lead Changes",
        desc: "When a new driver takes the lead",
      },
      {
        id: "race_fastest_lap",
        label: "Fastest Lap",
        desc: "New fastest lap of the race",
      },
    ],
  },
  {
    title: "Favorite Drivers",
    categories: [
      {
        id: "favorite_driver_position",
        label: "Position Changes",
        desc: "When your favorite drivers gain or lose positions",
      },
      {
        id: "favorite_driver_incident",
        label: "Incidents",
        desc: "When your favorite drivers are involved in incidents or go out",
      },
      {
        id: "favorite_driver_pit",
        label: "Pit Stops",
        desc: "When your favorite drivers make a pit stop",
      },
    ],
  },
  {
    title: "Reminders",
    categories: [
      {
        id: "schedule_reminder",
        label: "Schedule Reminders",
        desc: "Get reminded before races and qualifying sessions start",
      },
    ],
  },
];

const ALL_CATEGORY_IDS = CATEGORY_GROUPS.flatMap(g => g.categories.map(c => c.id));

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function NotificationSettings() {
  const {
    permission,
    isSubscribed,
    isSupported,
    isEnabled,
    subscription,
    error: pushError,
    isLoading: pushLoading,
    requestPermissionAndSubscribe,
    unsubscribe,
  } = usePushNotifications();

  const { preferences, isLoading, updatePreferences, updatePreferencesImmediate } =
    useNotificationPreferences();

  const addDemoToast = useNotificationToastStore(state => state.addDemoToast);
  const { devices, deleteDevice } = useDeviceSubscriptions();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (!isSupported) {
    return (
      <div className="rounded-md bg-slate-900 border border-slate-600 p-4">
        <p className="text-sm text-slate-200">
          Push notifications are not supported in this browser.
        </p>
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="rounded-md bg-slate-900 border border-slate-600 p-4">
        <p className="text-sm text-slate-200">
          Push notifications are not configured on the server.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-4">
        <LoadingCircle className="h-6 w-6" />
        <div className="text-slate-200">Loading preferences...</div>
      </div>
    );
  }

  const handleToggleCategory = (category: string) => {
    if (!preferences) return;
    const enabled = preferences.enabledCategories.includes(category);
    const newCategories = enabled
      ? preferences.enabledCategories.filter(c => c !== category)
      : [...preferences.enabledCategories, category];
    updatePreferences({ enabledCategories: newCategories });
  };

  const handleEnableAll = () => {
    updatePreferences({ enabledCategories: ALL_CATEGORY_IDS });
  };

  const handleDisableAll = () => {
    updatePreferences({ enabledCategories: [] });
  };

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="space-y-6 w-full max-w-full">
      {/* 1. Push Notifications */}
      <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
        <h3 className="mb-4 text-base font-semibold text-slate-200">
          Push Notifications
        </h3>

        {pushError && (
          <div className="mb-4 rounded-md bg-slate-900 border border-red-600 p-4">
            <p className="text-sm text-red-400">{pushError}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${
                isSubscribed ? "bg-green-400" : "bg-slate-500"
              }`}
            />
            <p className="text-sm font-medium text-slate-200">
              {isSubscribed ? "Active" : "Disabled"}
            </p>
            <span className="text-xs text-slate-500">({permission})</span>
          </div>

          {!isSubscribed ? (
            <button
              type="button"
              onClick={() => void requestPermissionAndSubscribe({ source: "settings" })}
              disabled={pushLoading}
              className="rounded-md bg-cPurple-600 px-4 py-2 text-sm font-medium text-white hover:bg-cPurple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cPurple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pushLoading ? "Enabling..." : "Enable Notifications"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void unsubscribe()}
              className="rounded-md bg-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
            >
              Disable Notifications
            </button>
          )}
        </div>
      </div>

      {/* Registered Devices */}
      {devices.length > 0 && (
        <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
          <h3 className="mb-4 text-base font-semibold text-slate-200">
            Registered Devices
          </h3>
          <div className="space-y-3">
            {devices.map(device => {
              const displayLabel =
                device.label ||
                `${device.browser || "Unknown"} on ${device.platform || "unknown"}`;
              const isCurrentDevice = subscription?.endpoint === device.endpoint;
              return (
                <div
                  key={device.id}
                  className="flex items-center justify-between gap-3 rounded-md bg-slate-800/50 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-slate-200 truncate">{displayLabel}</p>
                      {isCurrentDevice && (
                        <span className="shrink-0 text-[10px] font-medium text-cPurple-400 bg-cPurple-400/10 px-1.5 py-0.5 rounded">
                          This device
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Last active{" "}
                      {new Date(device.lastUsedAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  {confirmDeleteId === device.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          deleteDevice(device.id);
                          setConfirmDeleteId(null);
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs text-slate-500 hover:text-slate-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(device.id)}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {preferences && (
        <>
          {!isSubscribed && (
            <p className="text-sm text-slate-400 italic">
              These preferences will apply when you enable push notifications on any
              device.
            </p>
          )}

          {/* 2. In-App Notifications */}
          <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
            <h3 className="mb-4 text-base font-semibold text-slate-200">
              In-App Notifications
            </h3>

            <ToggleRow
              label="Keep on screen until dismissed"
              description="Notifications won't auto-dismiss"
              checked={preferences.toastPersistent}
              onChange={checked =>
                updatePreferencesImmediate({ toastPersistent: checked })
              }
            />

            {!preferences.toastPersistent && (
              <div className="mt-3">
                <p className="text-sm text-slate-200 mb-2">Display duration</p>
                <SegmentedControl
                  options={[
                    { value: 3, label: "3s" },
                    { value: 5, label: "5s" },
                    { value: 8, label: "8s" },
                    { value: 15, label: "15s" },
                    { value: 30, label: "30s" },
                    { value: 60, label: "60s" },
                  ]}
                  value={preferences.toastDisplaySeconds}
                  onChange={v => updatePreferences({ toastDisplaySeconds: v })}
                />
              </div>
            )}

            <div className="mt-4">
              <button
                type="button"
                onClick={addDemoToast}
                className="rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-600 transition-colors"
              >
                Send test notification
              </button>
            </div>
          </div>

          {/* 3. Notification Categories */}
          <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-200">
                Notification Categories
              </h3>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleEnableAll}
                  className="text-xs text-cPurple-400 hover:text-cPurple-300 transition-colors"
                >
                  Enable All
                </button>
                <button
                  type="button"
                  onClick={handleDisableAll}
                  className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Disable All
                </button>
              </div>
            </div>

            <div className="space-y-5">
              {CATEGORY_GROUPS.map(group => (
                <div key={group.title}>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    {group.title}
                  </p>
                  <div className="space-y-0.5">
                    {group.categories.map(cat => (
                      <ToggleRow
                        key={cat.id}
                        label={cat.label}
                        description={cat.desc}
                        checked={preferences.enabledCategories.includes(cat.id)}
                        onChange={() => handleToggleCategory(cat.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4. Quiet Hours */}
          <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
            <h3 className="mb-4 text-base font-semibold text-slate-200">Quiet Hours</h3>

            <ToggleRow
              label="Enable Quiet Hours"
              description="Suppress notifications during specified hours"
              checked={preferences.quietHoursEnabled}
              onChange={checked =>
                updatePreferencesImmediate({ quietHoursEnabled: checked })
              }
            />

            {preferences.quietHoursEnabled && (
              <div className="mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="quiet-hours-start"
                      className="mb-1 block text-sm font-medium text-slate-200"
                    >
                      Start Time
                    </label>
                    <input
                      id="quiet-hours-start"
                      type="time"
                      value={preferences.quietHoursStart ?? ""}
                      onChange={e =>
                        updatePreferences({ quietHoursStart: e.target.value })
                      }
                      className="block w-full rounded-md border border-slate-500 bg-black text-slate-100 shadow-sm focus:border-cPurple-500 focus:ring-cPurple-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="quiet-hours-end"
                      className="mb-1 block text-sm font-medium text-slate-200"
                    >
                      End Time
                    </label>
                    <input
                      id="quiet-hours-end"
                      type="time"
                      value={preferences.quietHoursEnd ?? ""}
                      onChange={e =>
                        updatePreferences({ quietHoursEnd: e.target.value })
                      }
                      className="block w-full rounded-md border border-slate-500 bg-black text-slate-100 shadow-sm focus:border-cPurple-500 focus:ring-cPurple-500 sm:text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Times are in {localTimezone}
                </p>
              </div>
            )}
          </div>

          {/* 5. Driver Settings */}
          <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
            <h3 className="mb-4 text-base font-semibold text-slate-200">
              Driver Settings
            </h3>

            <ToggleRow
              label="Only notify for favorite drivers"
              description="Limit driver-specific notifications to your favorites"
              checked={preferences.notifyFavoriteDriversOnly}
              onChange={checked =>
                updatePreferencesImmediate({ notifyFavoriteDriversOnly: checked })
              }
            />

            <div className="mt-3">
              <p className="text-sm text-slate-200 mb-1">Position change threshold</p>
              <p className="text-xs text-slate-500 mb-2">
                Notify when a favorite driver moves up or down by this many positions in
                a single lap
              </p>
              <SegmentedControl
                options={[
                  { value: 1, label: "1" },
                  { value: 3, label: "3" },
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                ]}
                value={preferences.positionChangeThreshold}
                onChange={v => updatePreferences({ positionChangeThreshold: v })}
              />
            </div>
          </div>

          {/* 6. Schedule Reminders */}
          <div className="rounded-lg border border-slate-600 bg-slate-900 p-6 w-full">
            <h3 className="mb-4 text-base font-semibold text-slate-200">
              Schedule Reminders
            </h3>

            <p className="text-xs text-slate-500 mb-2">
              How far in advance to receive race and qualifying reminders
            </p>
            <SegmentedControl
              options={[
                { value: 15, label: "15 min" },
                { value: 30, label: "30 min" },
                { value: 60, label: "1 hour" },
                { value: 120, label: "2 hours" },
              ]}
              value={preferences.scheduleReminderMinutes}
              onChange={v => updatePreferences({ scheduleReminderMinutes: v })}
            />
          </div>
        </>
      )}
    </div>
  );
}
